import { AllSpritesheets } from './spritesheets/generated';
import { LocationSpritesheets } from './spritesheets/pois.config';
import { MONSTER_CONFIGS } from './monsters.config';
import { BIOME_REGISTRY } from './biomes.config';
import { DUNGEON_REGISTRY } from './dungeons.config';
import { LOCATION_REGISTRY } from './locations.config';
import { floorTilesets, normalizeLayers } from './tileset.config';
import { HUB_MAPS } from './hub-map.config';
import { MusicId, SfxId } from './audio.config';

export interface AssetBundle {
  spritesheets: string[];
  textures: string[];
  music: MusicId[];
  sfx: SfxId[];
}

const ALL_NAMES: string[] = [
  ...AllSpritesheets.map((s) => s.name),
  ...LocationSpritesheets.map((s) => s.name),
];

const LOCATION_SHEET_NAMES = LocationSpritesheets.map((s) => s.name);

// Shared sheet groups always available regardless of context. UI/icon atlases,
// the player rig, generic spell VFX and the placeholder rig.
const CORE_SHEET_PREFIXES = [
  'icons',
  'icons-more',
  'rune-icons',
  'lumephons',
  'effects',
  'placeholders',
];
// Standalone effect sheet whose name does not share the `effects-` prefix.
const CORE_EXTRA_SHEETS = ['fire'];

// Known configId prefixes against which a sheet name is matched. Longest match
// wins so `shoebill-chick-*` is not captured by `shoebill`.
const KNOWN_CONFIG_IDS: string[] = [
  ...Object.keys(MONSTER_CONFIGS),
  ...CORE_SHEET_PREFIXES,
];

function longestMatchingConfigId(sheetName: string): string | undefined {
  let best: string | undefined;
  for (const id of KNOWN_CONFIG_IDS) {
    if (sheetName === id || sheetName.startsWith(`${id}-`)) {
      if (best === undefined || id.length > best.length) {
        best = id;
      }
    }
  }
  return best;
}

function sheetsForConfigId(id: string): string[] {
  return ALL_NAMES.filter((n) => longestMatchingConfigId(n) === id);
}

function tilesetTextures(tileset: string): string[] {
  const mapping = floorTilesets[tileset];
  if (!mapping) {
    return [];
  }
  const layers = new Set<string>();
  for (const layer of Object.values(mapping)) {
    for (const name of normalizeLayers(layer)) {
      layers.add(name);
    }
  }
  return [...layers].map((name) => `/tiles/${name}.png`);
}

// biomeId -> boss/mini-boss configIds, derived from dungeon custom floors and
// the lair locations injected through each biome's room templates.
function biomeBossConfigIds(): Record<string, Set<string>> {
  const result: Record<string, Set<string>> = {};
  const add = (biomeId: string, configId: string) => {
    (result[biomeId] ??= new Set()).add(configId);
  };

  for (const dungeon of Object.values(DUNGEON_REGISTRY)) {
    for (const floor of dungeon.initialConfig.customFloors ?? []) {
      if (floor.bossMonsterId && floor.biomeId) {
        add(floor.biomeId, floor.bossMonsterId);
      }
    }
  }

  for (const biome of Object.values(BIOME_REGISTRY)) {
    for (const template of biome.roomTemplates ?? []) {
      for (const loc of template.locations) {
        const miniBossId = LOCATION_REGISTRY[loc.locationId]?.miniBossId;
        if (miniBossId) {
          add(biome.id, miniBossId);
        }
      }
    }
  }

  return result;
}

function buildCoreBundle(): AssetBundle {
  const spritesheets = new Set<string>();
  for (const prefix of CORE_SHEET_PREFIXES) {
    for (const name of sheetsForConfigId(prefix)) {
      spritesheets.add(name);
    }
  }
  for (const name of CORE_EXTRA_SHEETS) {
    if (ALL_NAMES.includes(name)) {
      spritesheets.add(name);
    }
  }
  // POI/location sheets are placed by every biome's default location pool.
  for (const name of LOCATION_SHEET_NAMES) {
    spritesheets.add(name);
  }
  return { spritesheets: [...spritesheets], textures: [], music: [], sfx: [] };
}

function buildHubBundle(): AssetBundle {
  const textures = new Set<string>();
  for (const map of Object.values(HUB_MAPS)) {
    if (map.awakeBackgroundUrl) {
      textures.add(map.awakeBackgroundUrl);
    }
    if (map.dreamBackgroundUrl) {
      textures.add(map.dreamBackgroundUrl);
    }
    if (map.collisionMaskUrl) {
      textures.add(map.collisionMaskUrl);
    }
    if (map.dreamBubble?.maskUrl) {
      textures.add(map.dreamBubble.maskUrl);
    }
  }
  return { spritesheets: [], textures: [...textures], music: [], sfx: [] };
}

function buildBiomeBundles(): Record<string, AssetBundle> {
  const bosses = biomeBossConfigIds();
  const bundles: Record<string, AssetBundle> = {};

  for (const biome of Object.values(BIOME_REGISTRY)) {
    const spritesheets = new Set<string>();
    for (const configId of Object.keys(biome.spawnWeights)) {
      for (const name of sheetsForConfigId(configId)) {
        spritesheets.add(name);
      }
    }
    for (const configId of bosses[biome.id] ?? []) {
      for (const name of sheetsForConfigId(configId)) {
        spritesheets.add(name);
      }
    }

    const textures = new Set(tilesetTextures(biome.tileset));
    if (biome.transitionImage) {
      textures.add(biome.transitionImage);
    }

    bundles[`biome:${biome.id}`] = {
      spritesheets: [...spritesheets],
      textures: [...textures],
      music: [biome.musicId],
      sfx: [],
    };
  }

  return bundles;
}

export const ASSET_BUNDLES: Record<string, AssetBundle> = {
  core: buildCoreBundle(),
  hub: buildHubBundle(),
  ...buildBiomeBundles(),
};

export const SPRITESHEET_TO_BUNDLE: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const [bundleId, bundle] of Object.entries(ASSET_BUNDLES)) {
    for (const name of bundle.spritesheets) {
      map[name] = bundleId;
    }
  }
  return map;
})();

if (import.meta.env.DEV) {
  const assigned = new Set(Object.keys(SPRITESHEET_TO_BUNDLE));
  const orphans = ALL_NAMES.filter((n) => !assigned.has(n));
  if (orphans.length > 0) {
    // eslint-disable-next-line no-console
    console.warn('[asset-bundles] orphan spritesheets:', orphans);
  }
}
